<script setup lang="ts">
/**
 * "Абонементы" pane of the Services tab: the subscription type catalog
 * (packages of visits valid for a number of days).
 *
 * Infinite-scrolls GET `/subscription-types`. Each row shows the plan name over
 * the service types it covers, validity in days, the visit cap (or the
 * "unlimited" flag when there is none) and the catalog price. Covered type ids
 * are resolved to "<category> <duration>" labels through the same catalogs the
 * service forms use; each catalog is only fetched when the viewer holds its
 * view role, so a missing role falls back to a bare id label instead of a 403.
 * Layout follows `docs/sketches/desktop/service-types-table.html` (right pane)
 * and `docs/sketches/mobile/service-types-table.html`.
 */
import { computed, markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import TwoLineTextCell from '@/components/table/cell/TwoLineTextCell.vue'
import CountCell from '@/components/table/cell/CountCell.vue'
import CheckFlagCell from '@/components/table/cell/CheckFlagCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import SubscriptionTypeMobileCell from '@/components/table/cell/SubscriptionTypeMobileCell.vue'
import SubscriptionTypeMobileScreen from '@/components/main/subscriptionTypeCard/SubscriptionTypeMobileScreen.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { useCoveredTypeLabels } from '@/composables/useCoveredTypeLabels'
import { ResourceAccessRole } from '@/models/resource_access_role'
import {
  deleteSubscriptionType,
  fetchSubscriptionTypes,
  isUnlimitedSubscriptionType,
  type SubscriptionType,
} from '@/api/subscriptionTypes'
import { subscriptionTypesPollingInterval } from '@/config/api'
import { moneyColumnLabel } from '@/utils/formatMoney'
import { useSubscriptionTypeCardStore } from '@/stores/subscription_type_card_store'
import { useAddSubscriptionTypeFormStore } from '@/stores/add_subscription_type_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'

const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()

interface SubscriptionTypeRow {
  // Row for the subscription types table, mapped from GET `/subscription-types`.

  id: string
  rowNumber: number
  name: string
  durationDays: number
  numberOfServices: number | null
  unlimited: boolean
  coveredTypes: string
  // Covered service types as "<category> <duration>" labels, comma-separated.

  price?: number
  // Catalog price in major units; `undefined` when none is set.

  _raw: SubscriptionType
  // Full API record; used by the row edit handler to pre-fill the form.
}

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['subscriptionTypes'],
  queryFn: ({ pageParam }) => fetchSubscriptionTypes(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: subscriptionTypesPollingInterval,
  refetchIntervalInBackground: false,
  enabled: computed(() => hasRole(ResourceAccessRole.SubscriptionTypesView)),
})

const canView = computed(() => hasRole(ResourceAccessRole.SubscriptionTypesView))

const { labelsFor } = useCoveredTypeLabels()

const data = computed<SubscriptionTypeRow[]>(() => {
  const rows: SubscriptionTypeRow[] = []
  let index = 0
  for (const page of queryData.value?.pages ?? []) {
    for (const type of page.items) {
      index += 1
      rows.push({
        id: type.id,
        rowNumber: index,
        name: type.name,
        durationDays: type.duration,
        numberOfServices: type.number_of_services,
        unlimited: isUnlimitedSubscriptionType(type),
        coveredTypes: labelsFor(type.business_service_type_ids).join(', '),
        price: type.price?.price,
        _raw: type,
      })
    }
  }
  return rows
})

const columns = computed<Column<SubscriptionTypeRow>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'name',
    label: t('tabs.subscriptionTypes.colNameAndTypes'),
    component: markRaw(TwoLineTextCell),
    props: (row) => ({ title: row.name, subtitle: row.coveredTypes }),
  },
  {
    key: 'duration',
    label: t('common.daysShort'),
    component: markRaw(CountCell),
    props: (row) => ({ value: row.durationDays }),
    width: '4rem',
    align: 'right',
  },
  {
    key: 'visits',
    label: t('common.lessonsShort'),
    component: markRaw(CountCell),
    props: (row) => ({ value: row.numberOfServices }),
    width: '4rem',
    align: 'right',
  },
  {
    key: 'unlimited',
    label: '∞',
    component: markRaw(CheckFlagCell),
    props: (row) => ({ value: row.unlimited }),
    width: '4rem',
    align: 'center',
  },
  {
    key: 'price',
    label: moneyColumnLabel(t('common.price')),
    component: markRaw(MoneyCell),
    props: (row) => ({
      amount: row.price,
      precision: 0,
      fractionDigits: 2,
      emptyPlaceholder: '–',
    }),
    width: '7rem',
    align: 'center',
  },
])

const mobileColumns: Column<SubscriptionTypeRow>[] = [
  {
    key: 'card',
    label: '',
    component: markRaw(SubscriptionTypeMobileCell),
    props: (row) => ({
      index: row.rowNumber,
      name: row.name,
      durationDays: row.durationDays,
      numberOfServices: row.numberOfServices,
      coveredTypes: row.coveredTypes,
      price: row.price,
    }),
  },
]

function rowKeyFn(row: unknown): string {
  return (row as SubscriptionTypeRow).id
}

const subscriptionTypeCardStore = useSubscriptionTypeCardStore()
const addSubscriptionTypeFormStore = useAddSubscriptionTypeFormStore()
const queryClient = useQueryClient()

const selectedRowKey = computed<string | null>(
  () => subscriptionTypeCardStore.selectedSubscriptionTypeId,
)

function onRowClick(row: unknown): void {
  clearInfoViewSelections('subscriptionType')
  subscriptionTypeCardStore.selectSubscriptionType((row as SubscriptionTypeRow).id)
}

function onRowEditHandler(row: unknown): void {
  const type = (row as SubscriptionTypeRow)._raw
  clearInfoViewSelections('addSubscriptionTypeForm')
  addSubscriptionTypeFormStore.openForEdit(type.id, {
    name: type.name,
    durationDays: type.duration,
    numberOfServices: type.number_of_services,
    price: type.price?.price ?? null,
    businessServiceTypeIds: [...type.business_service_type_ids],
  })
}

const deleteMutation = useMutation({ mutationFn: (id: string) => deleteSubscriptionType(id) })

function onRowDeleteHandler(row: unknown): void {
  const r = row as SubscriptionTypeRow
  if (!confirm(t('tabs.subscriptionTypes.confirmDelete', { name: r.name }))) return
  deleteMutation.mutate(r.id, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subscriptionTypes'] })
      if (subscriptionTypeCardStore.selectedSubscriptionTypeId === r.id)
        subscriptionTypeCardStore.clearSelection()
    },
  })
}
</script>

<template>
  <p v-if="!canView" class="text-tufte-muted">{{ t('tabs.subscriptionTypes.noAccess') }}</p>
  <p v-else-if="isPending" class="text-tufte-muted">{{ t('tabs.subscriptionTypes.loading') }}</p>
  <p v-else-if="isError" class="text-red-600">
    {{
      t('tabs.subscriptionTypes.loadError', {
        message: (error as Error)?.message ?? t('common.unknownError'),
      })
    }}
  </p>
  <p v-else-if="data.length === 0" class="text-tufte-muted">
    {{ t('tabs.subscriptionTypes.empty') }}
  </p>
  <template v-else>
    <p class="subscription-types-summary">
      {{ t('units.subscription', data.length) }} · {{ t('tabs.subscriptionTypes.subtitle') }}
    </p>
    <DynamicTable
      :class="isMobile ? 'subscription-types-table--mobile' : 'subscription-types-table'"
      :columns="isMobile ? (mobileColumns as Column<unknown>[]) : (columns as Column<unknown>[])"
      :data="data"
      :has-next-page="hasNextPage"
      :is-fetching-next-page="isFetchingNextPage"
      :on-load-more="fetchNextPage"
      :on-row-click="onRowClick"
      :row-key="rowKeyFn"
      :selected-row-key="isMobile ? null : selectedRowKey"
      :on-row-edit="
        isMobile || !hasRole(ResourceAccessRole.SubscriptionTypesEdit)
          ? undefined
          : onRowEditHandler
      "
      :on-row-delete="
        isMobile || !hasRole(ResourceAccessRole.SubscriptionTypesDelete)
          ? undefined
          : onRowDeleteHandler
      "
      :pinnable-header="!isMobile"
      storage-key="subscription-types"
    />
  </template>

  <SubscriptionTypeMobileScreen
    v-if="isMobile && subscriptionTypeCardStore.hasSelection"
    :subscription-type-id="subscriptionTypeCardStore.selectedSubscriptionTypeId!"
    @close="subscriptionTypeCardStore.clearSelection()"
  />
</template>

<style scoped>
.subscription-types-summary {
  margin: 0 0 0.5rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
}

/* Desktop: fill the whole info view so the add button sits over the table's right edge; the name column takes the leftover width. */
.subscription-types-table :deep(.dynamic-table) {
  width: 100%;
}

.subscription-types-table--mobile :deep(.dynamic-table) {
  width: 100%;
}

.subscription-types-table--mobile :deep(thead) {
  display: none;
}

.subscription-types-table--mobile :deep(td) {
  padding: 0.7rem 1.25rem;
}
</style>
